// Principal, IssuerConfig, ClaimRule and Role (E01-S6-T2; contracts 1.0 section 8, identity part).
// Shapes only: verifying a token and applying a mapping are E15's.
import { z } from "zod";
import { Id, NonEmpty, strictObject } from "./primitives.js";
/**
 * The six roles. What each may do is the matrix in D-38. client-reviewer (ADR-031) answers and waives
 * source issues on its own tenant's packs and does nothing else.
 */
export const Role = z.enum([
    "associate",
    "manager",
    "content-owner",
    "client-reviewer",
    "tenant-admin",
    "platform-admin",
]);
export const Population = z.enum(["client", "employee", "admin", "content-owner", "service"]);
/** Hosts where plain http is allowed, so the hermetic Keycloak works. Nothing public. */
export function isLocalHost(hostname) {
    const h = hostname.toLowerCase();
    return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".localhost");
}
function parseUrl(s) {
    try {
        return new URL(s);
    }
    catch {
        return null;
    }
}
/** An https URL, or an http URL whose host is local. */
export const SecureUrl = z.string().refine((s) => {
    const u = parseUrl(s);
    if (!u || !u.hostname)
        return false;
    return u.protocol === "https:" || (u.protocol === "http:" && isLocalHost(u.hostname));
}, "expected an https URL (http only for localhost, 127.0.0.1, [::1] or *.localhost)");
/** Where a native or web client receives the sign-in result: a SecureUrl, the extension or the desktop app. */
export const RedirectUri = z.string().refine((s) => {
    const u = parseUrl(s);
    if (!u)
        return false;
    if (u.protocol === "chrome-extension:" || u.protocol === "waypoint:")
        return u.hostname.length > 0;
    return SecureUrl.safeParse(s).success;
}, "expected an https URL, a local http URL, chrome-extension:// or waypoint://");
/**
 * A claim lookup (a path of keys, so nested and URL-named claims are unambiguous) with optional
 * split, map and default — or a constant. Never both: both arms are strict, so a rule carrying
 * path and const matches neither (D-36).
 */
export const ClaimLookup = strictObject({
    path: z.array(NonEmpty).min(1),
    split: z.string().min(1).optional(),
    map: z.record(NonEmpty, NonEmpty).optional(),
    default: NonEmpty.optional(),
});
export const ClaimConst = strictObject({ const: NonEmpty });
export const ClaimRule = z.union([ClaimLookup, ClaimConst]);
export const ClientKind = z.enum(["web", "extension", "desktop"]);
export const IssuerConfig = strictObject({
    id: Id,
    issuer: SecureUrl,
    audience: NonEmpty,
    jwksUri: SecureUrl.optional(), // discovery when omitted
    tenantId: Id, // an entry is bound to one tenant (D-37)
    population: Population,
    maxRoles: z.array(Role).min(1), // a ceiling applied after mapping (D-37)
    claims: strictObject({
        org: ClaimRule,
        user: ClaimRule,
        displayName: ClaimRule.optional(),
        roles: ClaimRule,
        entitlements: ClaimRule.optional(),
        region: ClaimRule.optional(),
        userType: ClaimRule.optional(),
        session: ClaimRule.optional(), // the sid claim (D-39)
    }),
    clients: z
        .partialRecord(ClientKind, strictObject({
        clientId: NonEmpty,
        scopes: z.array(NonEmpty),
        redirectUris: z.array(RedirectUri),
    }))
        .optional(),
});
export const Principal = strictObject({
    tenantId: Id,
    orgId: NonEmpty,
    userId: NonEmpty,
    displayName: NonEmpty.optional(), // travels to a model only as user-profile (D-62)
    roles: z.array(Role).min(1),
    entitlements: z.array(NonEmpty),
    region: NonEmpty.optional(),
    userType: NonEmpty.optional(),
    population: Population,
    sessionId: NonEmpty.optional(),
});
//# sourceMappingURL=identity.js.map